import {
  CheckableAvatarSelectItem,
  DropdownSelect,
  IconNames,
} from '@dixa/lib-shared-habla';
import type { ReadSearchApiArg } from 'lib-search-types';
import { ampli } from 'lib-shared-tracking';
import { useCallback, useMemo, useState } from 'react';
import { useUpdateEffect } from 'react-use';

import { useLazyReadSearchInfiniteQuery } from 'apis/search';
import { toBoolean } from 'utils/formatting/string';
import { useAppSearchParams } from 'utils/url/useAppSearchParams';

import { ConversationsFilterDropdownItem } from '../../conversationsFiltersToolbar.types';

type ConversationsAgentFilterDropdownItem = ConversationsFilterDropdownItem<
  string | number,
  {
    /** The filter dropdown menu icon. */
    readonly icon?: IconNames;
  }
>;

const staticDropdownItems: readonly ConversationsAgentFilterDropdownItem[] = [
  { id: 0, name: 'Unassigned', icon: 'user-slash' },
  { id: 1, name: 'Assigned', icon: 'user' },
];

export const ConversationsAgentFilter = (): JSX.Element => {
  const {
    params: { agent: agentParam, assigned: assignedParam },
    patchParams,
    unsetParams,
  } = useAppSearchParams('/conversations');

  const isAssigned = assignedParam ? toBoolean(assignedParam) : undefined;

  const agentsParam = useMemo(
    () =>
      typeof agentParam !== 'undefined'
        ? typeof agentParam === 'string'
          ? [agentParam]
          : agentParam
        : [],
    [agentParam],
  );

  const initialArg: ReadSearchApiArg<'users'> = useMemo(
    () => ({
      scope: 'users',
      sortBy: { field: 'display_name', order: 'asc' },
      filters: {
        strategy: 'all',
        filters: [
          {
            field: 'user_type',
            operator: 'is_one',
            values: [{ value: 'member' }],
          },
        ],
      },
    }),
    [],
  );

  const [readSearchUsers, { results: agents, hasNextPage, fetchNextPage }] =
    useLazyReadSearchInfiniteQuery<'users'>();

  const [searchTerm, setSearchTerm] = useState<string>();

  useUpdateEffect(() => {
    readSearchUsers(
      {
        ...initialArg,
        query: searchTerm,
      },
      { debounceTime: 300 },
    );
  }, [readSearchUsers, initialArg, searchTerm]);

  const dropdownItems = useMemo<
    readonly ConversationsAgentFilterDropdownItem[]
  >(
    () => [
      ...staticDropdownItems,
      ...agents.map(({ id, name, email, phone_number, avatar_url }) => ({
        id,
        name: name || email || phone_number || id,
        avatar_url,
      })),
    ],
    [agents],
  );

  const selectedDropdownItemIds = useMemo<
    readonly ConversationsAgentFilterDropdownItem['id'][]
  >(
    () =>
      isAssigned === undefined
        ? agentsParam
        : staticDropdownItems
            .filter(({ id }) => Boolean(id) === isAssigned)
            .map(({ id }) => id),
    [agentsParam, isAssigned],
  );

  const handleSearch = useCallback((value?: string) => {
    setSearchTerm(value?.trim() || undefined);
  }, []);

  const handleToggle = useCallback(
    (isOpen: boolean) => {
      if (isOpen && !agents.length) {
        readSearchUsers({
          ...initialArg,
          query: searchTerm,
        });
      } else {
        setSearchTerm(undefined);
      }
    },
    [readSearchUsers, initialArg, searchTerm, agents.length],
  );

  const handleChange = useCallback(
    (item: ConversationsAgentFilterDropdownItem, checked: boolean) => {
      const isAgent = typeof item.id === 'string';

      patchParams({
        set: {
          ...(isAgent
            ? {
                agent: checked
                  ? [...agentsParam, item.id]
                  : agentsParam.filter((id) => item.id !== id),
                assigned: undefined,
              }
            : {
                agent: undefined,
                assigned: checked ? Boolean(item.id).toString() : undefined,
              }),
        },
        unset: ['limit', 'offset'],
      });

      ampli.conversationsFilterToggled({
        filter: 'agent',
        value: isAgent ? 'id' : Boolean(item.id) ? 'assigned' : 'unassigned',
        checked,
      });
    },
    [agentsParam, patchParams],
  );

  const handleClear = useCallback(() => {
    unsetParams(['agent', 'assigned', 'limit', 'offset']);
    ampli.conversationsFilterCleared({ filter: 'agent' });
  }, [unsetParams]);

  return (
    <DropdownSelect
      keepOpen
      hasSearch
      noArrow={false}
      // TODO: [TEAMS-9726] Remove re-casting when fixed in Habla
      items={dropdownItems as ConversationsAgentFilterDropdownItem[]}
      selectedItems={
        selectedDropdownItemIds as ConversationsAgentFilterDropdownItem['id'][]
      }
      itemComponent={CheckableAvatarSelectItem}
      buttonProps={{
        variant: 'tertiary',
        label: selectedDropdownItemIds.length || undefined,
      }}
      dropdownProps={{ style: { minWidth: 280 } }}
      pagination={{
        previousEnable: false,
        nextEnable: hasNextPage,
        infinite: true,
        onNext: () => Promise.resolve(fetchNextPage()),
      }}
      onChange={handleChange}
      onClear={handleClear}
      onSearch={handleSearch}
      onToggle={handleToggle}
    >
      Agents
    </DropdownSelect>
  );
};
